import { createFileRoute } from "@tanstack/react-router";
import { PortalLayout } from "@/components/portal-layout";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, MapPin } from "lucide-react";

export const Route = createFileRoute("/admin/my-schedule")({
  component: MySchedulePage,
});

function MySchedulePage() {
  const { user } = useAuth();

  const sessionsQ = useQuery({
    queryKey: ["admin-my-schedule", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: myClasses } = await supabase
        .from("classes")
        .select("id, name, subject, schedule_note")
        .eq("teacher_id", user!.id);

      if (!myClasses?.length) return [];

      const classIds = myClasses.map((c) => c.id);
      const classMap = Object.fromEntries(myClasses.map((c) => [c.id, c]));

      const now = new Date();
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() + 7);

      const { data: sessions } = await supabase
        .from("class_sessions")
        .select("id, class_id, starts_at, ends_at, room, topic")
        .in("class_id", classIds)
        .gte("starts_at", now.toISOString())
        .lte("starts_at", weekEnd.toISOString())
        .order("starts_at");

      return (sessions ?? []).map((s) => ({
        ...s,
        className: classMap[s.class_id]?.name ?? "",
        subject: classMap[s.class_id]?.subject ?? "",
      }));
    },
  });

  const sessions = sessionsQ.data ?? [];
  const grouped = sessions.reduce(
    (acc, s) => {
      const day = new Date(s.starts_at).toLocaleDateString("vi-VN", {
        weekday: "long",
        day: "numeric",
        month: "numeric",
      });
      if (!acc[day]) acc[day] = [];
      acc[day].push(s);
      return acc;
    },
    {} as Record<string, typeof sessions>,
  );

  return (
    <PortalLayout
      portal="admin"
      title="Lịch dạy"
      breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Lịch dạy" }]}
    >
      {sessions.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="size-10 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
          <h3 className="font-semibold text-lg">Chưa có buổi học</h3>
          <p className="text-sm text-muted-foreground mt-1">Tạo lớp và lịch học để bắt đầu.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([day, dayS]) => (
            <div key={day}>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">{day}</h3>
              <div className="space-y-2">
                {dayS.map((s) => (
                  <div key={s.id} className="p-4 bg-card border rounded-xl flex items-center gap-4">
                    <div className="shrink-0 text-center">
                      <div className="text-sm font-semibold">
                        {new Date(s.starts_at).toLocaleTimeString("vi-VN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {new Date(s.ends_at).toLocaleTimeString("vi-VN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{s.className}</div>
                      {s.topic && (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">{s.topic}</div>
                      )}
                    </div>
                    {s.room && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                        <MapPin className="size-3" strokeWidth={1.5} />
                        {s.room}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
